import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { Socket } from 'socket.io-client';
import { useAuth } from '../auth/AuthContext';
import { connectMessagesSocket } from './messagesSocketClient';
import { playMessageNotificationSound } from './notificationSound';

const MessagesSocketContext = createContext<Socket | null>(null);

// Le ticket d'auth est à usage unique : socket.io-client ne peut pas s'auto-reconnecter
// avec le même handshake, donc on réémet une connexion complète (nouveau ticket) au lieu
// de laisser la reconnexion automatique de la lib (qui rejouerait le ticket déjà consommé).
export function MessagesSocketProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [socket, setSocket] = useState<Socket | null>(null);
  const socketRef = useRef<Socket | null>(null);

  useEffect(() => {
    if (!user) {
      socketRef.current?.disconnect();
      socketRef.current = null;
      setSocket(null);
      return;
    }

    let cancelled = false;
    let current: Socket | null = null;

    async function connect() {
      const next = await connectMessagesSocket();
      if (cancelled) {
        next.disconnect();
        return;
      }
      current = next;
      socketRef.current = next;
      next.on('disconnect', (reason) => {
        if (reason === 'io server disconnect' || reason === 'io client disconnect') return;
        if (!cancelled) void connect();
      });
      next.on('new-message', () => playMessageNotificationSound());
      setSocket(next);
    }

    void connect();

    return () => {
      cancelled = true;
      current?.disconnect();
      socketRef.current = null;
    };
  }, [user]);

  return (
    <MessagesSocketContext.Provider value={socket}>{children}</MessagesSocketContext.Provider>
  );
}

export function useMessagesSocket(): Socket | null {
  return useContext(MessagesSocketContext);
}
